"use client";
import {
  hasNFCFilter,
  isAttachedToDOM,
  nonFloorMatchingResourcesFilter,
  nonOfficeMatchingResourcesFilter
} from "@/features/map/utils/utils-map";
import React, { useRef, useState } from "react";
import { useMap } from "@/features/map/MapProvider";
import { ResourceEditingOptions } from "@gettactic/map-builder/src/types";
import { useResources } from "@gettactic/hooks";
import { IResource } from "@gettactic/api";
import { useBooleanState } from "@gettactic/helpers";
import { MapEditorState } from "@gettactic/map-builder/src/components/map/contexts/MapEditorContext";
import { useQueryClient } from "@tanstack/react-query";

export interface UseMapViewSvgEditReturn {
  startEdit: (
    svgMapEditRef: React.MutableRefObject<SVGSVGElement | null | undefined>
  ) => void;
  endEdit: () => void;
  selectedNodes: Array<string>;
  floorSpecificResources: IResource[] | undefined;
  mapEditAllMapResources: Array<string>;
  addMapEditResourceSelected: (id: string) => void;
  clearMapEditSelected: () => void;
  triggerNonMatchOffice: () => void;
  triggerNonMatchFloor: () => void;
  nonOfficeMatchingMapResources: string[];
  nonFloorMatchingMapResources: string[];
  triggerHasNFC: () => void;
  svgMapEditRef: React.MutableRefObject<SVGSVGElement | null | undefined>;
  mapResourcesEditOptions: ResourceEditingOptions;
  setMapResourcesEditOptions: React.Dispatch<
    React.SetStateAction<ResourceEditingOptions>
  >;
  isResourcePanelEditorOpen: boolean;
  openResourceEditPanel: () => void;
  closeResourceEditPanel: () => void;
  openMapBuilderEditor: () => void;
  closeMapBuilderEditor: () => void;
  isMapBuilderEditorOpen: boolean;
  mapBuilderRef: MapEditorState | undefined | null;
  setMMapBuilderRef: (
    mapEditorState: MapEditorState | undefined | null
  ) => void;
}

export const useMapViewSvgEdit = (): UseMapViewSvgEditReturn => {
  const { isMapBuilderMap, resources, currentOffice, floor } = useMap();
  const queryClient = useQueryClient();
  const svgMapEditRef = useRef<SVGSVGElement | null | undefined>(null);

  const hasNfcOfficeResources = resources ? hasNFCFilter(resources) : [];
  const [nonMatchingOffice, setNonMatchingOffice] = useState<boolean>(false);
  const [nonMatchingFloor, setNonMatchingFloor] = useState<boolean>(false);
  const [hasNFC, setHasNFC] = useState<boolean>(false);
  const [mapResourcesEditOptions, setMapResourcesEditOptions] =
    useState<ResourceEditingOptions>({
      isOpen: false,
      resourceIds: []
    });
  const isMapBuilderEditorOpen = useBooleanState();
  const isMapEditing =
    isMapBuilderEditorOpen.state || mapResourcesEditOptions.isOpen;
  const mapBuilderRef = React.useRef<MapEditorState | undefined | null>(
    undefined
  );
  const { resources: floorSpecificResources } = useResources(
    {
      officeId: currentOffice.id,
      types: [
        "desk",
        "workspace",
        "meeting_room",
        "parking_lot",
        "parking_space",
        "area"
      ],
      parentId: floor ? floor.id : ""
    },
    {
      enabled: isMapEditing
    }
  );

  const [mapEditAllMapResources, setMapEditAllMapResources] = useState<
    Array<string>
  >([]);

  const [_mapEditSelectedList, setMapEditSelectedList] = useState<
    Array<string>
  >([]);

  const hasNfcNodeElements: NodeListOf<Element> | null =
    typeof window !== "undefined"
      ? document.querySelectorAll(".has-nfc")
      : null;
  const nonMatchingFloorNodeElements: NodeListOf<Element> | null =
    typeof window !== "undefined"
      ? document.querySelectorAll(".non-matching-floor")
      : null;
  const nonMatchingOfficeNodeElements: NodeListOf<Element> | null =
    typeof window !== "undefined"
      ? document.querySelectorAll(".non-matching-office")
      : null;

  const mapEditSelectedList = isMapBuilderMap
    ? mapResourcesEditOptions.resourceIds || []
    : _mapEditSelectedList || [];

  const nonOfficeMatchingMapResources = resources
    ? nonOfficeMatchingResourcesFilter(mapEditAllMapResources, resources)
    : [];

  const nonFloorMatchingMapResources =
    resources && floorSpecificResources
      ? nonFloorMatchingResourcesFilter(
          mapEditAllMapResources,
          resources,
          floorSpecificResources.elements
        )
      : [];

  function addMapEditResourceSelected(id: string) {
    setMapEditSelectedList((currentArray) => {
      const currentIndex = currentArray.indexOf(id);
      let newValue: string[] = [];

      if (currentIndex >= 0) {
        newValue = currentArray.filter((x) => x !== id);
      } else {
        newValue = [id, ...currentArray];
      }
      return newValue;
    });
  }

  function clearMapEditSelected() {
    setMapResourcesEditOptions({ isOpen: false, resourceIds: [] });
    setMapEditSelectedList([]);
  }

  function addActiveClass(
    elements: NodeListOf<Element> | null,
    cssClass: string
  ): void {
    if (!elements) return;
    elements.forEach((element: Element) => {
      element.classList.add(cssClass);
    });
  }

  function removeActiveClass(
    elements: NodeListOf<Element> | null,
    cssClass: string
  ): void {
    if (!elements) return;
    elements.forEach((element: Element) => {
      element.classList.remove(cssClass);
    });
  }

  function triggerNonMatchOffice() {
    if (nonMatchingOffice) {
      addActiveClass(
        nonMatchingOfficeNodeElements,
        "non-matching-office-active"
      );
    } else {
      removeActiveClass(
        nonMatchingOfficeNodeElements,
        "non-matching-office-active"
      );
    }
    setNonMatchingOffice(!nonMatchingOffice);
  }

  // Highlight resources that are not assigned to the current floor
  function triggerNonMatchFloor() {
    if (nonMatchingFloor) {
      addActiveClass(nonMatchingFloorNodeElements, "non-matching-floor-active");
    } else {
      removeActiveClass(
        nonMatchingFloorNodeElements,
        "non-matching-floor-active"
      );
    }
    setNonMatchingFloor(!nonMatchingFloor);
  }

  const startEdit = (
    _svgMapEditRef: React.MutableRefObject<SVGSVGElement | null | undefined>
  ) => {
    svgMapEditRef.current = _svgMapEditRef.current;
    if (!svgMapEditRef.current) return;
    const nodeElements =
      svgMapEditRef.current.querySelectorAll<HTMLElement>(`[id^="reso_"]`);
    const handlePointerUp = (e: PointerEvent) => {
      const path = e?.composedPath() as SVGElement[];
      const element = path.find((node) =>
        "id" in node ? node.id.startsWith("reso_") : false
      );
      const elementId = element ? element.id : null;
      if (elementId) {
        addMapEditResourceSelected(elementId);
      }
    };

    const mapElements: string[] = [];
    Array.from(nodeElements).forEach((element) => {
      if (!mapElements.includes(element.id)) {
        mapElements.push(element.id);
      }
    });

    //setMapEditAllMapResources(mapElements);

    Array.from(nodeElements).map((element) => {
      if (!isAttachedToDOM(element)) return;
      if (typeof window === "undefined") return;
      const elementClass = element.getAttribute("class");

      // Only allow the class to be set during the initialization, otherwise it overwrites classes appended during usage
      if (!elementClass?.includes("map-edit-element")) {
        element.setAttribute("class", "map-edit-element");
      }

      // If the class has been set, we are looping and need to take care not to overwrite active status states
      if (elementClass?.includes("map-edit-element")) {
        // If we have a mismatched office resource <=> map node, then add a non-matching class
        if (
          nonOfficeMatchingMapResources.includes(element.id) &&
          !elementClass?.includes("non-matching-office")
        ) {
          const newClass = elementClass?.concat(" non-matching-office");
          element.setAttribute("class", newClass);
        }

        // If we have a mismatched floor resource <=> map node, then add a non-matching class
        if (
          nonFloorMatchingMapResources?.includes(element.id) &&
          !elementClass?.includes("non-matching-floor")
        ) {
          const newClass = elementClass?.concat(" non-matching-floor");
          element.setAttribute("class", newClass);
        }

        // If the resource has an attached NFC tag or QR code, add a class
        if (
          hasNfcOfficeResources?.find((r) => r.id === element.id) &&
          !elementClass?.includes("has-nfc")
        ) {
          const newClass = elementClass?.concat(" has-nfc");
          element.setAttribute("class", newClass);
        }
      }

      element.addEventListener("pointerup", handlePointerUp);

      return { element };
    });
  };
  const endEdit = () => {
    setMapEditSelectedList([]);
    clearMapEditSelected();
  };

  function triggerHasNFC(): void {
    if (hasNFC) {
      addActiveClass(hasNfcNodeElements, "has-nfc-active");
    } else {
      removeActiveClass(hasNfcNodeElements, "has-nfc-active");
    }
    setHasNFC(!hasNFC);
  }

  const openResourceEditPanel = () => {
    setMapResourcesEditOptions({
      isOpen: true,
      resourceIds: []
    });
  };

  const closeResourceEditPanel = () => {
    setMapResourcesEditOptions({
      isOpen: false,
      resourceIds: []
    });
  };

  const openMapBuilderEditor = () => {
    queryClient.invalidateQueries({
      queryKey: [
        "organizations.resourcesAll",
        currentOffice?.id ?? "",
        ["area"],
        floor?.id ?? ""
      ]
    });
    isMapBuilderEditorOpen.true();
    setMapResourcesEditOptions({
      isOpen: false,
      resourceIds: []
    });
  };

  const closeMapBuilderEditor = () => {
    isMapBuilderEditorOpen.false();
    setMapResourcesEditOptions({
      isOpen: false,
      resourceIds: []
    });
  };

  const setMMapBuilderRef = (
    mapEditorState: MapEditorState | undefined | null
  ) => {
    mapBuilderRef.current = mapEditorState;
  };

  const isResourcePanelEditorOpen = mapResourcesEditOptions.isOpen;

  return {
    startEdit,
    endEdit,
    selectedNodes: mapEditSelectedList,
    floorSpecificResources: floorSpecificResources?.elements || [],
    mapEditAllMapResources,
    addMapEditResourceSelected,
    clearMapEditSelected,
    triggerNonMatchOffice,
    triggerNonMatchFloor,
    nonOfficeMatchingMapResources,
    nonFloorMatchingMapResources,
    triggerHasNFC,
    svgMapEditRef,
    mapResourcesEditOptions,
    setMapResourcesEditOptions,
    isResourcePanelEditorOpen,
    openResourceEditPanel,
    closeResourceEditPanel,
    openMapBuilderEditor,
    closeMapBuilderEditor,
    setMMapBuilderRef,
    isMapBuilderEditorOpen: isMapBuilderEditorOpen.state,
    mapBuilderRef: mapBuilderRef.current
  };
};
